;(async () => {
  if (
    !window.recharge ||
    !window.bear?.bundleProductMap ||
    !window.bear?.collectionMap ||
    !window.bear?.cartProducts
  ) {
    console.log('Recharge or Bear data not found')
    return
  }

  // initialize Recharge API
  recharge.init({
    storeIdentifier: window.bear.storeIdentifier,
  })

  const delay = (time) => {
    return new Promise(res => {
      setTimeout(res,time)
    })
  }

  /**
   * Fetch cart data from Shopify
   * @returns {Object} - Cart data from Shopify
   */
  const fetchCartData = async () => {
    const res = await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
    })
    return res.json()
  }

  /**
   * Replace cart items with new cart data
   * @param {Object} cartData - Cart data to replace existing cart items with
   * @param {Array} cartData.items - Array of cart items
   * @param {String} cartData.items.id - Cart item ID
   * @param {Number} cartData.items.quantity - Cart item quantity
   * @param {Object} cartData.items.properties - Cart item properties
   * @param {String} cartData.items.selling_plan - Cart item selling plan ID
   * @returns {Object} - Response from cart/add.js
   */
  const replaceCartItems = async (cartData) => {
    const clearRes = await fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const clearResJson = await clearRes.json()
    // eslint-disable-next-line no-console
    console.log('clearResJson', clearResJson);

    await delay(1000)

    const addRes = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartData),
    })
    const addResJson = await addRes.json()
    // eslint-disable-next-line no-console
    console.log('addResJson', addResJson);

    await delay(1000)

    return addRes
  }

  /**
   * Get bundle items from cart data
   * @returns {Object} - Object containing cart items with properties from Recharge SDK
   * @returns {Array} items - Array of cart items
   * @returns {String} items.id - Cart item ID
   * @returns {Number} items.quantity - Cart item quantity
   * @returns {Object} items.properties - Cart item properties
   * @returns {String} items.selling_plan - Cart item selling plan ID
   */
  const getBundleItems = async (cartData, bundleKey = 'default') => {
    const bundleProduct = window.bear.bundleProductMap[bundleKey]
    const collection = window.bear.collectionMap[bundleKey]

    if (!bundleProduct) {
      console.log('Bundle product not found')
      return
    }

    if (!collection) {
      console.log('Collection not found')
      return
    }

    const bundleProductData = {
      productId: bundleProduct.id,
      variantId: bundleProduct.variants[0].id,
      handle: bundleProduct.handle,
    }

    const bundle = {
      externalVariantId: bundleProductData.variantId,
      externalProductId: bundleProductData.productId,
      selections: cartData.items.map((item) => {
        return {
          collectionId: collection,
          externalProductId: item.product_id.toString(),
          externalVariantId: item.variant_id.toString(),
          quantity: item.quantity,
          // "sellingPlan": item.sellingPlan,
        }
      }),
    }

    // create bundle items from Recharge SDK
    const rcItems = await recharge.bundle.getDynamicBundleItems(
      bundle,
      bundleProductData.handle
    )

    // rebuild cart items with selling plan and properties
    const cartItems = cartData.items.map((item) => {
      // construct cart item object
      const obj = {
        id: item.id,
        quantity: item.quantity,
        properties: item.properties,
      }

      // attach selling plan if already exists on item
      if (item.selling_plan_allocation?.selling_plan) {
        obj.selling_plan = item.selling_plan_allocation.selling_plan.id
      }

      // pull properties from rcItems into cartData items
      const rcItem = rcItems.find(
        (cartItem) => parseInt(cartItem.id) === parseInt(item.id)
      )

      if (rcItem?.properties) {
        obj.properties = {
          ...obj.properties,
          ...rcItem.properties,
        }
      }

      return obj
    })

    return { items: cartItems }
  }

  /**
   * Get selling plans for products in cart
   * @returns {Array} - Array of products with selling plans
   */
  const getProductSellingPlans = () => {
    return window.bear.cartProducts.map((product) => {
      return {
        product_id: product.id,
        sellingPlans: product.selling_plan_groups
          .filter((group) => group.app_id === '294517')
          .map((group) => {
            const sellingPlan = group.selling_plans[0]
            return {
              id: sellingPlan.id,
              name: sellingPlan.name,
              options: sellingPlan.options,
              price_adjustments: sellingPlan.price_adjustments,
            }
          }),
      }
    })
  }

  /**
   * Get selling plan for product based on option
   * @param {String} productId - Product ID
   * @param {Object} option - Selling plan option
   * @returns {Object} - Selling plan object
   */
  const getProductSellingPlanByOption = (productId, option) => {
    // get array of products with selling plans
    const productSellingPlans = getProductSellingPlans()

    // create translation map to get selling plan based on option
    const translationMap = {
      weekly: '1 week',
      biweekly: '2 week',
      monday: 'Monday',
      sunday: 'Sunday',
    }

    const productPlanGroup = productSellingPlans.find(
      (product) => product.product_id === productId
    )

    if (!productPlanGroup) {
      return
    }

    return productPlanGroup.sellingPlans.find((plan) => {
      return (
        plan.name.includes(translationMap[option.frequency]) &&
        plan.name.includes(translationMap[option.charge_day])
      )
    })
  }

  /**
   * Build new cart items with selling plans based on option
   * @param {Object} cartData - Cart data from Shopify
   * @param {Object} option - Selling plan option to update to
   * @param {String} option.type - 'onetime' or 'subscription'
   * @param {String} option.frequency - 'weekly' or 'biweekly'
   * @param {String} option.charge_day - 'monday' or 'sunday'
   * @returns {Array} - Array of cart items with selling plans
   */
  const buildItemsWithSellingPlans = (cartData, option) => {
    return cartData.items.map((item) => {
      if (!option || option.type === 'onetime') {
        return {
          id: item.id,
          quantity: item.quantity,
          properties: item.properties,
        }
      }

      const sellingPlan = getProductSellingPlanByOption(item.product_id, option)

      if (!sellingPlan) {
        // eslint-disable-next-line no-console
        console.log(
          `No selling plan found for product ${
            item.product_id
          } with option ${JSON.stringify(option)}`
        )

        return {
          id: item.id,
          quantity: item.quantity,
          properties: item.properties,
        }
      }

      return {
        id: item.id,
        quantity: item.quantity,
        properties: item.properties,
        selling_plan: sellingPlan?.id,
      }
    })
  }

  /**
   * Add bundle properties to items in cart
   * @param {string} bundleKey - key for different regional bundle
   * @returns {Object} - Response from cart
   */
  const addBundlePropsToItems = async (bundleKey = 'default') => {
    // fetch latest cart data
    const cartData = await fetchCartData()

    // if all items in cart have _rc_bundle property, return
    if (
      cartData.items.every((item) => {
        return !!item.properties._rc_bundle
      })
    ) {
      console.log('All items in cart are already bundle items')
      return
    }

    const bundleItems = await getBundleItems(cartData, bundleKey)

    return await replaceCartItems(bundleItems)
  }

  /**
   * Rebuild cart items with selling plans
   * @param {Object} option - Selling plan option to update to
   * @param {String} option.type - 'onetime' or 'subscription'
   * @param {String} option.frequency - 'weekly' or 'biweekly'
   * @param {String} option.charge_day - 'monday' or 'sunday'
   */
  const getItemsWithSellingPlans = async (option = { type: 'onetime' }) => {
    // Throw error if invalid option type provided
    if (
      !option?.type ||
      ['onetime', 'subscription'].indexOf(option.type) === -1
    ) {
      console.log(
        'Invalid option type provided. Must be "onetime" or "subscription".'
      )
      return
    }

    if (option.type === 'subscription') {
      // Throw error if invalid option frequency provided
      if (['weekly', 'biweekly'].indexOf(option.frequency) === -1) {
        console.log(
          'Invalid option frequency provided. Must be "weekly" or "biweekly".'
        )
        return
      }

      // Throw error if invalid option charge_day provided
      if (['monday', 'sunday'].indexOf(option.charge_day) === -1) {
        console.log(
          'Invalid option charge_day provided. Must be "monday" or "sunday".'
        )
        return
      }
    }

    // fetch cart data
    const cartData = await fetchCartData()

    // build new cart data with updated selling plans based on options
    return buildItemsWithSellingPlans(cartData, option)
  }

  /**
   * Update cart items with updated selling plans
   * @param {Object} option - Selling plan option to update to
   * @param {String} option.type - 'onetime' or 'subscription'
   * @param {String} option.frequency - 'weekly' or 'biweekly'
   * @param {String} option.charge_day - 'monday' or 'sunday'
   */
  const updateItemsSellingPlans = async (options) => {
    const cartData = await getItemsWithSellingPlans(options)

    if (!cartData) {
      return
    }

    return await replaceCartItems({ items: cartData })
  }

  // make functions available globally
  window.bear.addBundlePropsToItems = addBundlePropsToItems
  window.bear.getItemsWithSellingPlans = getItemsWithSellingPlans
  window.bear.updateItemsSellingPlans = updateItemsSellingPlans
  // window.bear.updateItemsSellingPlans({type: 'subscription', frequency: 'weekly', charge_day: 'monday'})
})()
